import { create } from 'zustand';
import { supabase } from '../supabase';
import { isOrgAiEnabledNow, notifyAiDisabled, isAiDisabledError } from '../hooks/useOrgAiEnabled';
import type {
  MeetingRecording,
} from '../types/meetingRecording';

// ─── Helper: get user org data ───────────────────────────────

async function getUserData() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('User not authenticated');

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();

  if (!profile?.organization_id) throw new Error('Organization not found');

  return {
    userId: userData.user.id,
    organizationId: profile.organization_id,
  };
}

// ─── Store Interface ─────────────────────────────────────────

interface MeetingRecordingStore {
  // Recordings list
  recordings: MeetingRecording[];
  recordingsLoading: boolean;
  recordingsLoadingMore: boolean;
  recordingsError: string | null;

  // Pagination + search server-side
  recordingsHasMore: boolean;
  recordingsTotalCount: number | null;
  recordingsSearch: string;
  recordingsPageSize: number;

  // Single recording detail
  currentRecording: MeetingRecording | null;
  currentRecordingLoading: boolean;

  // ── Fetch actions ──
  /** Carica la prima pagina (resetta lo state). Usa setSearch per filtri server-side. */
  fetchAllRecordings: () => Promise<void>;
  /** Append della pagina successiva (per infinite scroll). No-op se !hasMore o gia' loading. */
  fetchMoreRecordings: () => Promise<void>;
  /** Aggiorna query di ricerca server-side e ricarica prima pagina. */
  setRecordingsSearch: (q: string) => Promise<void>;
  fetchRecordingsForDeal: (dealId: string) => Promise<void>;
  fetchRecordingsForLead: (leadId: string) => Promise<void>;
  fetchRecordingsForClient: (clientId: string) => Promise<void>;
  fetchRecordingsForProject: (projectId: string) => Promise<void>;
  fetchRecordingById: (id: string) => Promise<MeetingRecording | null>;

  // ── AI summary ──
  generateSummary: (recordingId: string) => Promise<boolean>;

  // ── Delete ──
  deleteRecording: (recordingId: string) => Promise<boolean>;

  // ── Calendar matching ──
  fetchRecordingByCalendarEventId: (calendarEventId: string, meetingUrl?: string) => Promise<MeetingRecording | null>;
  linkRecordingToEntity: (recordingId: string, updates: { client_id?: string | null; deal_id?: string | null; lead_id?: string | null; project_id?: string | null }) => Promise<boolean>;

  // ── AI Processing (centralized engine) ──
  processAI: (recordingId: string, actions: string[], force?: boolean) => Promise<boolean>;

  // ── Drawer visibility (used by Layout to hide global AI widget) ──
  isMeetingDrawerOpen: boolean;
  setMeetingDrawerOpen: (open: boolean) => void;
}

// ─── Store Implementation ────────────────────────────────────

// SELECT con join: client, deal, project, lead (lead e' in clients).
// Usato sia per fetchAllRecordings che per fetchMoreRecordings.
const LIST_SELECT = '*, client:clients!client_id(id, business_name), deal:deals!deal_id(id, title), project:projects!project_id(id, name), lead:clients!lead_id(id, business_name)';

export const useMeetingRecordingStore = create<MeetingRecordingStore>((set, get) => ({
  // State
  recordings: [],
  recordingsLoading: false,
  recordingsLoadingMore: false,
  recordingsError: null,
  recordingsHasMore: false,
  recordingsTotalCount: null,
  recordingsSearch: '',
  recordingsPageSize: 30,
  currentRecording: null,
  currentRecordingLoading: false,
  isMeetingDrawerOpen: false,
  setMeetingDrawerOpen: (open) => set({ isMeetingDrawerOpen: open }),

  // ══════════════════════════════════════════════════════════
  // FETCH RECORDINGS (paginated server-side + optional search)
  // ══════════════════════════════════════════════════════════

  fetchAllRecordings: async () => {
    const { recordingsSearch, recordingsPageSize } = get();
    set({ recordings: [], recordingsLoading: true, recordingsError: null, recordingsHasMore: false });
    try {
      const { organizationId } = await getUserData();
      let query = supabase
        .from('meeting_recordings')
        .select(LIST_SELECT, { count: 'exact' })
        .eq('organization_id', organizationId);

      const q = recordingsSearch.trim();
      if (q) {
        // Cerca in titolo (server-side, case-insensitive)
        query = query.ilike('meeting_title', `%${q}%`);
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(0, recordingsPageSize - 1);

      if (error) throw error;
      const rows = (data || []) as MeetingRecording[];
      set({
        recordings: rows,
        recordingsTotalCount: count ?? null,
        recordingsHasMore: rows.length === recordingsPageSize && (count == null || rows.length < count),
      });
    } catch (err: any) {
      console.error('Error fetching all meeting recordings:', err);
      set({ recordingsError: err.message });
    } finally {
      set({ recordingsLoading: false });
    }
  },

  fetchMoreRecordings: async () => {
    const { recordings, recordingsHasMore, recordingsLoading, recordingsLoadingMore, recordingsPageSize, recordingsSearch } = get();
    if (!recordingsHasMore || recordingsLoading || recordingsLoadingMore) return;
    set({ recordingsLoadingMore: true });
    try {
      const { organizationId } = await getUserData();
      const offset = recordings.length;
      let query = supabase
        .from('meeting_recordings')
        .select(LIST_SELECT)
        .eq('organization_id', organizationId);

      const q = recordingsSearch.trim();
      if (q) query = query.ilike('meeting_title', `%${q}%`);

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + recordingsPageSize - 1);

      if (error) throw error;
      const rows = (data || []) as MeetingRecording[];
      set((state) => ({
        recordings: [...state.recordings, ...rows],
        recordingsHasMore: rows.length === recordingsPageSize,
      }));
    } catch (err: any) {
      console.error('Error fetching more meeting recordings:', err);
    } finally {
      set({ recordingsLoadingMore: false });
    }
  },

  setRecordingsSearch: async (q: string) => {
    set({ recordingsSearch: q });
    await get().fetchAllRecordings();
  },

  fetchRecordingsForDeal: async (dealId: string) => {
    set({ recordings: [], recordingsLoading: true, recordingsError: null });
    try {
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ recordings: (data || []) as MeetingRecording[] });
    } catch (err: any) {
      console.error('Error fetching meeting recordings:', err);
      set({ recordingsError: err.message });
    } finally {
      set({ recordingsLoading: false });
    }
  },

  // 🚨 7 set 2026: lead e cliente sono la STESSA riga di `clients` (li distingue solo
  // `stato_cliente`), quindi lo stesso id può finire in `lead_id` oppure in `client_id`
  // a seconda di come è stata fatta l'associazione. L'interfaccia di associazione scrive
  // SEMPRE `client_id` (AssociateMeetingModal, MeetingAssociationInline), mentre qui si
  // cercava solo `lead_id`: risultato, le call associate non comparivano mai sulla scheda
  // del lead. In produzione erano **70 registrazioni invisibili**.
  // La scheda offerta faceva già la cosa giusta (DealNotesSection cerca su entrambe le
  // colonne): qui ci si allinea. Vale in entrambi i versi, perché una call associata
  // quando l'anagrafica era un lead deve restare visibile dopo la conversione a cliente.
  fetchRecordingsForLead: async (leadId: string) => {
    set({ recordings: [], recordingsLoading: true, recordingsError: null });
    try {
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .or(`lead_id.eq.${leadId},client_id.eq.${leadId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ recordings: (data || []) as MeetingRecording[] });
    } catch (err: any) {
      console.error('Error fetching meeting recordings:', err);
      set({ recordingsError: err.message });
    } finally {
      set({ recordingsLoading: false });
    }
  },

  // Speculare a fetchRecordingsForLead: stessa riga, stesse due colonne possibili.
  fetchRecordingsForClient: async (clientId: string) => {
    set({ recordings: [], recordingsLoading: true, recordingsError: null });
    try {
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .or(`client_id.eq.${clientId},lead_id.eq.${clientId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ recordings: (data || []) as MeetingRecording[] });
    } catch (err: any) {
      console.error('Error fetching meeting recordings:', err);
      set({ recordingsError: err.message });
    } finally {
      set({ recordingsLoading: false });
    }
  },

  fetchRecordingsForProject: async (projectId: string) => {
    set({ recordings: [], recordingsLoading: true, recordingsError: null });
    try {
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ recordings: (data || []) as MeetingRecording[] });
    } catch (err: any) {
      console.error('Error fetching meeting recordings:', err);
      set({ recordingsError: err.message });
    } finally {
      set({ recordingsLoading: false });
    }
  },

  fetchRecordingById: async (id: string) => {
    set({ currentRecordingLoading: true });
    try {
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      const recording = data as MeetingRecording;
      set((state) => {
        const exists = state.recordings.some(r => r.id === recording.id);
        return {
          currentRecording: recording,
          recordings: exists
            ? state.recordings.map(r => r.id === recording.id ? recording : r)
            : [recording, ...state.recordings],
        };
      });
      return recording;
    } catch (err: any) {
      console.error('Error fetching meeting recording:', err);
      return null;
    } finally {
      set({ currentRecordingLoading: false });
    }
  },



  // ══════════════════════════════════════════════════════════
  // AI SUMMARY
  // ══════════════════════════════════════════════════════════

  generateSummary: async (recordingId: string) => {
    // Interruttore IA dell'azienda: spento = nessun riassunto IA.
    if (!isOrgAiEnabledNow()) { notifyAiDisabled(); return false; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Non autenticato');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vexa-ai-summary`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ recording_id: recordingId }),
        }
      );

      if (!response.ok) {
        const result = await response.json();
        if (isAiDisabledError(result)) notifyAiDisabled();
        throw new Error(result.error || 'Errore generazione riassunto');
      }

      // Refresh the recording to get the AI data
      await get().fetchRecordingById(recordingId);
      return true;
    } catch (err: any) {
      console.error('Error generating AI summary:', err);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════
  // AI PROCESSING (centralized engine — flusia-core /api/ai/process-recording)
  // ══════════════════════════════════════════════════════════

  processAI: async (recordingId: string, actions: string[], force = false) => {
    // Interruttore IA dell'azienda: spento = nessuna elaborazione IA della registrazione.
    if (!isOrgAiEnabledNow()) { notifyAiDisabled(); return false; }
    try {
      const { organizationId } = await getUserData();
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Sessione utente non disponibile');

      const gateway = (import.meta.env.VITE_API_GATEWAY_URL as string | undefined) || 'https://api.flusia.it';
      const res = await fetch(`${gateway}/api/ai/process-recording`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          recording_id: recordingId,
          organization_id: organizationId,
          actions,
          force,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (isAiDisabledError(errBody)) notifyAiDisabled();
        throw new Error((errBody as { error?: string }).error || `HTTP ${res.status}`);
      }

      // Job in queue: aspetto un po' poi refresh (best effort, l'utente vede update via realtime)
      setTimeout(() => { void get().fetchRecordingById(recordingId); }, 3000);
      return true;
    } catch (err: any) {
      console.error('Error processing AI:', err);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════
  // DELETE RECORDING
  // ══════════════════════════════════════════════════════════

  deleteRecording: async (recordingId: string) => {
    try {
      const { userId, organizationId } = await getUserData();
      const recording = get().recordings.find(r => r.id === recordingId);

      // Audit log before deletion
      if (recording) {
        await supabase.from('audit_logs').insert({
          organization_id: organizationId,
          user_id: userId,
          entity_type: 'meeting_recording',
          entity_id: recordingId,
          entity_name: recording.meeting_title || 'Registrazione',
          action_type: 'delete',
          project_id: recording.project_id,
          metadata: {
            platform: recording.platform,
            meeting_url: recording.meeting_url,
            status: recording.status,
            duration_seconds: recording.duration_seconds,
            has_transcript: !!recording.full_transcript,
            has_summary: !!recording.ai_summary,
            created_at: recording.created_at,
          }
        });
      }

      const { error } = await supabase
        .from('meeting_recordings')
        .delete()
        .eq('id', recordingId);

      if (error) throw error;

      // Update local state
      set(state => ({
        recordings: state.recordings.filter(r => r.id !== recordingId),
      }));

      return true;
    } catch (err: any) {
      console.error('Error deleting meeting recording:', err);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════
  // CALENDAR MATCHING & ENTITY LINKING
  // ══════════════════════════════════════════════════════════

  fetchRecordingByCalendarEventId: async (calendarEventId: string, meetingUrl?: string) => {
    try {
      // Primary: match by calendar_event_id
      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .eq('calendar_event_id', calendarEventId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) return data as MeetingRecording;

      // Fallback: match by meeting_url if provided
      if (meetingUrl) {
        const { data: byUrl } = await supabase
          .from('meeting_recordings')
          .select('*')
          .eq('meeting_url', meetingUrl)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (byUrl) return byUrl as MeetingRecording;
      }

      return null;
    } catch (err) {
      console.error('Error fetching recording by calendar event:', err);
      return null;
    }
  },

  linkRecordingToEntity: async (recordingId: string, updates: { client_id?: string | null; deal_id?: string | null; lead_id?: string | null; project_id?: string | null }) => {
    try {
      const { error } = await supabase
        .from('meeting_recordings')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', recordingId);

      if (error) throw error;

      // Refresh the recording
      await get().fetchRecordingById(recordingId);
      return true;
    } catch (err: any) {
      console.error('Error linking recording to entity:', err);
      return false;
    }
  },

}));
